import React, { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  Popover,
  Avatar,
  Badge,
  Empty,
  theme,
  Button,
  Dropdown,
  message,
} from "antd";

import {
  DashboardOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  TruckOutlined,
  ClockCircleOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ToolOutlined,
  SettingOutlined,
} from "@ant-design/icons";

import { Link, Outlet, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { getFileUrl } from "../api/file";
import { logout } from "../helpers/auth/useSession";
import { setAccessToken } from "../helpers/auth/token";
import { UserRole } from "../lib/constants";

import { cn } from "../lib/utils";
import { getUserCompanies, selectCompany } from "../api/user";

const { Header, Sider, Content } = Layout;

export default function AppLayout({ user }) {
  const [collapsed, setCollapsed] = useState(false);
  const [companies, setCompanies] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function fetchCompanies() {
      try {
        const response = await getUserCompanies();
        setCompanies(response.companies);
      } catch (error) {
        message.error("Failed to load companies");
      }
    }
    fetchCompanies();
  }, []);

  const handleCompanySelect = async (companyId) => {
    try {
      const response = await selectCompany(companyId);
      setAccessToken(response.accessToken);
      message.success("Company selected successfully");
      window.location.reload();
    } catch (error) {
      message.error("Failed to select company");
    }
  };

  const companyMenu = (
    <Menu>
      {companies.map((company) => (
        <Menu.Item key={company.id} onClick={() => handleCompanySelect(company.id)}>
          {company.name}
        </Menu.Item>
      ))}
    </Menu>
  );

  function getItem(label, key, icon, children, type, onClick) {
    return {
      key,
      icon,
      children,
      label,
      type,
      onClick,
    };
  }

  const items = [
    getItem("App", "app", <DashboardOutlined />, [
      getItem(
        <div
          className={cn(
            location.pathname === "/" ? "text-orange-400" : "text-black"
          )}
        >
          Summary
        </div>,
        "/"
      ),
      getItem(
        <div
          className={cn(
            location.pathname.includes("projects")
              ? "text-orange-400"
              : "text-black"
          )}
        >
          Projects
        </div>,
        "projects"
      ),
      getItem(
        <div
          className={cn(
            location.pathname.includes("clients")
              ? "text-orange-400"
              : "text-black"
          )}
        >
          Clients
        </div>,
        "clients"
      ),
      ...(user.role !== UserRole.AccountManager ? [
        getItem(
          <div
            className={cn(
              location.pathname.includes("users")
                ? "text-orange-400"
                : "text-black"
            )}
          >
            Users
          </div>,
          "users"
        ),
      ] : []),
    ]),
    ...(user.role !== UserRole.AccountManager ? [
      getItem(
        <Link
          to="/equipments"
          className={cn(
            location.pathname.includes("equipments")
              ? "text-orange-400"
              : "text-black"
          )}
        >
          Equipments
        </Link>,
        "equipments",
        <TruckOutlined />,
        [
          getItem(<div>Precheck Form</div>, "/equipments-precheck-qxn"),
          getItem(<div>Precheck Report</div>, "/equipments-precheck-ans"),
        ]
      ),
      getItem(
        <Link to="/worklogs">Worklogs</Link>,
        "worklogs",
        <ClockCircleOutlined />
      ),
      getItem(
        <Link
          to="/equipment-accessories"
          className={cn(
            location.pathname.includes("equipment-accessories")
              ? "text-orange-400"
              : "text-black"
          )}
        >
          Accessories
        </Link>,
        "equipment-accessories",
        <ToolOutlined />
      ),
      getItem(
        <Link
          to="/settings"
          className={cn(
            location.pathname.includes("settings")
              ? "text-orange-400"
              : "text-black"
          )}
        >
          Settings
        </Link>,
        "settings",
        <SettingOutlined />
      ),
    ] : []),
  ];

  const onClick = (e) => {
    if (e) {
      navigate(e.key);
    }
  };

  const {
    token: { colorPrimary },
  } = theme.useToken();

  return (
    <Layout className="h-screen antialiased">
      <Header
        className="flex items-center  justify-between px-[16px] h-[48px]"
        style={{ backgroundColor: colorPrimary }}
      >
        <h3 className="text-white font-bold text-lg leading-8  flex flex-col justify-center cursor-pointer">
          TrakHire
        </h3>
        <div className="flex space-x-6 items-center">
          <Dropdown overlay={companyMenu} trigger={['click']}>
            <Button ghost>{user?.company?.name || "Select company"}</Button>
          </Dropdown>
          <QuestionCircleOutlined className="text-base text-white" />
          <Popover
            placement="bottom"
            title="Notifications"
            trigger={["click"]}
            content={<Notifications />}
          >
            <Badge
              //TODO: add notification count
              count={0}
              size="small"
              className="cursor-pointer "
              offset={[0, 0]}
            >
              <BellOutlined className="text-white text-base" />
            </Badge>
          </Popover>
          <div className="flex space-x-2 items-center">
            <Popover
              placement="bottom"
              trigger={"click"}
              content={
                <div className="flex flex-col gap-3">
                  <Link to="/settings">
                    {" "}
                    <Button>Settings</Button>
                  </Link>
                  <Button
                    onClick={async () => {
                      await logout();
                    }}
                  >
                    Logout
                  </Button>
                </div>
              }
            >
              <Avatar
                icon={<UserOutlined className="text-gray-500" />}
                src={user.avatar ? getFileUrl(user.avatar) : null}
                className="bg-white"
              />
            </Popover>
            <p className="  text-white leading-3">{user.firstName}</p>
          </div>
        </div>
      </Header>
      <Layout>
        <Sider
          width="208px"
          className="text-white !bg-white h-full"
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          trigger={
            !collapsed ? (
              <MenuFoldOutlined className="bg-white h-full w-full text-black px-auto flex justify-center" />
            ) : (
              <MenuUnfoldOutlined className="bg-white h-full w-full text-black mx-auto flex justify-center " />
            )
          }
        >
          <Menu
            defaultSelectedKeys={["/"]}
            defaultOpenKeys={["app"]}
            mode="inline"
            items={items}
            onClick={onClick}
          />
        </Sider>
        <Content className="overflow-y-auto overflow-x-clip">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

function Notifications(p) {
  return (
    <div>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<div>You have no notifications at the moment.</div>}
        className="h-full flex flex-col justify-center "
      />
    </div>
  );
}
